import ParentEvaluationInfo from './components/ParentEvaluationInfo';
import {
  Col,
  DatePicker,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  ModalProps,
  Row,
  Select,
  SelectProps,
  Typography
} from 'antd';
import { IClassroomEvaluation } from 'interfaces/classrooms';
import { replaceTimeZone } from 'services/date';
import { useCreate, useNotification, useOne } from '@refinedev/core';
import { useForm } from '@refinedev/antd';

export interface ReplacementCreateModalProps {
  evaluationResource: string;
  modalClose: () => void;
  modalProps: ModalProps;
  assignmentSelectorProps: SelectProps<{ value: string; label: string }>;
  assesmentSelectorProps: SelectProps<{ value: string; label: string }>;
  parentId: string;
}

const ReplacementCreateModal = ({
  evaluationResource,
  modalClose,
  modalProps,
  assignmentSelectorProps,
  assesmentSelectorProps,
  parentId
}: ReplacementCreateModalProps) => {
  const { open } = useNotification();
  const { mutateAsync: createEvaluation } = useCreate();
  const { data: evaluationData } = useOne<IClassroomEvaluation>({
    resource: evaluationResource,
    id: parentId
  });
  const { form, formProps } = useForm();
  const type = Form.useWatch('evaluation_type', form);

  const handleCreate = async () => {
    return form
      .validateFields()
      .then(async () => {
        const [starts_at, ends_at] = form.getFieldValue('dateRange');
        return createEvaluation({
          resource: evaluationResource,
          values: {
            ...form.getFieldsValue(),
            starts_at: replaceTimeZone(starts_at),
            ends_at: replaceTimeZone(ends_at),
            kind: 'replacement',
            passing_score: evaluationData?.data?.passing_score,
            parent_id: parentId
          }
        }).then(() => {
          modalClose();
          form.resetFields();
        });
      })
      .catch(() => {
        return open?.({
          type: 'error',
          message: 'Favor conferir todos os campos obrigatórios.',
          description: 'Error ao criar registro!'
        });
      });
  };

  const handleSelectType = () => {
    form.setFieldsValue({ evaluation_id: null });
  };

  return (
    <Modal
      {...modalProps}
      title="Criar nova avaliação substitutiva"
      cancelText="Cancelar"
      okText="Criar avaliação"
      className="!w-[1022px]"
      onOk={() => handleCreate()}
      destroyOnClose
    >
      <Form form={form} {...formProps} layout="vertical">
        <ParentEvaluationInfo evaluationData={evaluationData?.data} />
        <Divider />
        <Typography.Title level={5}>
          Informações gerais da avaliação substitutiva
        </Typography.Title>

        <Row gutter={[16, 16]}>
          <Col md={8}>
            <Form.Item
              label="Tipo"
              name="evaluation_type"
              rules={[
                {
                  required: true
                }
              ]}
            >
              <Select onChange={handleSelectType}>
                <Select.Option value="Assessment">Avaliação</Select.Option>
                <Select.Option value="Assignment">Atividade</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col md={8}>
            <Form.Item
              label="Avaliação"
              name="evaluation_id"
              rules={[
                {
                  required: true
                }
              ]}
            >
              {type === 'Assessment' ? (
                <Select
                  notFoundContent={
                    <Empty description="Não há avaliações cadastradas para essa disciplina" />
                  }
                  disabled={!type}
                  {...assesmentSelectorProps}
                  style={{ minWidth: 200 }}
                />
              ) : (
                <Select
                  notFoundContent={
                    <Empty description="Não há atividades cadastradas para essa disciplina" />
                  }
                  disabled={!type}
                  {...assignmentSelectorProps}
                  style={{ minWidth: 200 }}
                />
              )}
            </Form.Item>
          </Col>
          <Col md={8}>
            <Form.Item
              label="Nome"
              name="name"
              rules={[
                {
                  required: true
                }
              ]}
            >
              <Input style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col md={8}>
            <Form.Item
              label="Tentativas"
              name="max_attempts"
              rules={[
                {
                  required: true
                }
              ]}
            >
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col md={11}>
            <Form.Item label="Regra de liberação" name="rule">
              <Select style={{ minWidth: 300 }} defaultValue={null}>
                <Select.Option value={null}>
                  Alunos que não foram aprovados ou perderam a avaliação
                </Select.Option>
                <Select.Option value="classroom_enrollment">
                  Substitutiva especial
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Divider />
        <Row>
          <Typography.Title level={5}>Acesso</Typography.Title>
        </Row>
        <Row>
          <Typography.Paragraph>
            O período de acesso da avaliação deve está entre o inicio e fim da
            turma (dd/mm/aaaa - dd/mm/aaaa)
          </Typography.Paragraph>
        </Row>
        <Row>
          <Col md={20}>
            <Form.Item
              label="Acesso"
              name="dateRange"
              rules={[
                {
                  required: true
                }
              ]}
            >
              <DatePicker.RangePicker
                className="w-full"
                showTime
                placeholder={[
                  'Início (horário de Brasília)',
                  'Término (horário de Brasília)'
                ]}
                format="DD/MM/YYYY HH:mm"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ReplacementCreateModal;
